//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { rgba } from 'polished';
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { MediaAtomPositionMixins } from '@components/atoms/media/MediaAtomPositionMixins';
import {
  IMediaTypeOverlayIconProps,
  MediaTypeOverlayIcon,
} from '@components/atoms/overlay-icon/MediaTypeOverlayIcon';
import OverlayIcon from '@components/atoms/overlay-icon/OverlayIcon';
import RightsOverlayIcon from '@components/atoms/overlay-icon/RightsOverlayIcon';
import getGalleriesClientType from '@galleries/selectors/getGalleriesClientType';
import { IUiaModalClosedResponse } from '@legacy-components/common/universal-image-actions/models/events';
import { getIsUiaVideoClosedCaptionsEnabled } from '@legacy-components/common/universal-image-actions/selectors/getIsUiaVideoClosedCaptionsEnabled';
import { IContentItemMetadata } from '@models/IItemMetadata';
import ItemId from '@models/ItemId';
import {
  UniversalImageActionsView,
  UniversalImageActionsViewName,
} from '@models/UniversalImageActionsView';
import { UniversalItemType } from '@models/UniversalItemType';
import { IUiaLaunchOptions } from '@react-bridge/wrappers/ng-service-provider-types';
import { getDistributionEndpointLabel } from '@utils/distributionEndpointUtils';
import { isDefined } from '@utils/filterPredicates';
import { pluralize, toGrammaticallyCorrectList } from '@utils/stringUtils';
import { openUia } from '@utils/uia';

import { IContentItemDetails } from 'app/bulk-actions/model/AppState';
import {
  captionsInPprogressTooltip,
  captionsTurnedOnTooltip,
  muteIconTooltip,
} from 'app/constants/mute-tooltip-content';
export type MetadataItemDetails = Omit<IContentItemDetails, 'userTags'> & {
  isRejectedForMissingTerms?: boolean;
};
export interface IContentItem {
  metadata: IContentItemMetadata;
  videoId?: string;
}
interface Props {
  itemType: UniversalItemType;
  id: ItemId;
  itemDetails?: Partial<MetadataItemDetails>;
  mediaType?: IMediaTypeOverlayIconProps['type'];
  onUiaClose?: (response: IUiaModalClosedResponse) => void;
  isRejectedForMissingTerms?: boolean;
  isCopyrighted?: boolean;
  isCaptionInProgress?: boolean;
}

/**
 * The MetadataLayer atom is used exclusively in the Media atom to support
 * the media type icon in the top right of the image.
 */

export const MetadataLayer = ({
  id,
  isCaptionInProgress,
  isCopyrighted,
  itemDetails,
  itemType,
  mediaType,
  onUiaClose,
}: Props) => {
  const galleriesClientType = useSelector((state) =>
    getGalleriesClientType(state),
  );
  const isConnectClient = useSelector(
    (state) => state.session.features.share_v2,
  );
  const isVideoCaptionFeatureEnabled = useSelector(
    getIsUiaVideoClosedCaptionsEnabled,
  );

  const numProducts = itemDetails?.productTagsCount;
  const rightsStatus = itemDetails?.rightsStatus;
  const distributionEndpoints = itemDetails?.distributionEndpoints;
  const muteStatus = itemDetails?.muteStatus;
  const captionStatus = itemDetails?.captionStatus;
  const adsAuthorizationMessage = itemDetails?.adsAuthorizationMessage;
  const hasSparkCode = itemDetails?.hasSparkCode;

  const distributionEndpointsFormatted = (distributionEndpoints ?? [])
    .map((endpoint) =>
      getDistributionEndpointLabel(
        endpoint,
        galleriesClientType,
        isConnectClient,
      ),
    )
    .filter((label) => label !== '');

  const overlayClickHelper = (
    event: React.MouseEvent,
    launchOptions: IUiaLaunchOptions,
    clickType: UniversalImageActionsViewName,
  ) => {
    event.stopPropagation();
    event.preventDefault();
    openUia(
      launchOptions,
      UniversalImageActionsView[clickType].name,
      onUiaClose,
    );
  };

  return (
    <>
      {mediaType ? (
        <MediaTypeOverlayIcon isCopyrighted={isCopyrighted} type={mediaType} />
      ) : null}
      <StyledMetadataOverlayIconContainer>
        {numProducts && numProducts > 0 ? (
          <StyledOverlayIcon
            count={numProducts}
            iconName='product'
            onClick={(event) =>
              overlayClickHelper(event, { itemId: id, itemType }, 'Productize')
            }
            tooltipContent={`This image has ${pluralize(
              numProducts,
              'product',
            )}`}
          />
        ) : null}
        {distributionEndpointsFormatted.length > 0 ? (
          <StyledOverlayIcon
            count={distributionEndpointsFormatted.length}
            iconName='media-published'
            onClick={(event) =>
              overlayClickHelper(event, { itemId: id, itemType }, 'Home')
            }
            tooltipContent={`This image has been distributed to ${toGrammaticallyCorrectList(
              distributionEndpointsFormatted,
            )}`}
          />
        ) : null}
        {rightsStatus ? (
          <StyledRightsOverlayIcon
            isRejectedForMissingTerms={itemDetails.isRejectedForMissingTerms}
            onClick={(event) =>
              overlayClickHelper(
                event,
                { itemId: id, itemType },
                'RightsRequest',
              )
            }
            rightsStatus={rightsStatus}
          />
        ) : null}
        {isDefined(adsAuthorizationMessage) && (
          <StyledOverlayIcon
            iconName='bullhorn'
            tooltipContent={adsAuthorizationMessage}
          />
        )}
        {itemDetails?.hasSparkCode && (
          <StyledOverlayIcon
            iconName='tiktok-spark'
            tooltipContent='This content is eligible for Spark Ads'
          />
        )}
      </StyledMetadataOverlayIconContainer>

      <StyledVideoMetadataContainer data-media={mediaType}>
        {isVideoCaptionFeatureEnabled && captionStatus ? (
          <StyledOverlayIcon
            iconName='closed-caption-on'
            tooltipContent={captionsTurnedOnTooltip}
          ></StyledOverlayIcon>
        ) : null}

        {isVideoCaptionFeatureEnabled &&
        isCaptionInProgress &&
        !captionStatus ? (
          <StyledOverlayIcon
            className='caption-in-progress'
            iconName='loading-caption'
            tooltipContent={captionsInPprogressTooltip}
          ></StyledOverlayIcon>
        ) : null}

        {muteStatus && (
          <StyledOverlayIcon
            iconName='volume-off'
            tooltipContent={muteIconTooltip}
          ></StyledOverlayIcon>
        )}
      </StyledVideoMetadataContainer>
    </>
  );
};
const StyledVideoMetadataContainer = styled.div`
  ${MediaAtomPositionMixins.BottomRight};
  z-index: 1;
`;

const StyledMetadataOverlayIconContainer = styled.div`
  ${MediaAtomPositionMixins.BottomLeft};
  z-index: 1;
`;

const StyledOverlayIcon = styled(OverlayIcon)(
  ({ theme }) => `
  &.caption-in-progress > div {
    background-color: ${rgba(theme.colors.gray8, 0.6)}; 
  }
  display: inline-flex;
  margin-right: ${theme.spacings[1]};
  white-space: pre-wrap;
`,
);

const StyledRightsOverlayIcon = styled(RightsOverlayIcon)(
  ({ theme }) => `
  display: inline-flex;
  margin-right: ${theme.spacings[2]};
`,
);
